// Centralized analytics helper for mchatai.com.
//
// Single source of truth for the GA4 measurement id + a guarded trackEvent().
// See mchatai_macOS/docs/ANALYTICS_EVENT_SPEC.md for the canonical, cross-platform
// event catalog that web/iOS/macOS all mirror.
//
// Privacy posture (matches the apps — the "not creepy" contract):
//   - Anonymous product-interaction only. Never attach the Firebase uid/email.
//   - Never pass free text / content (prompts, goals, doc bodies, paths) as params.
//   - Params are enums, ids, booleans, and bucketed counts only.
//
// gtag itself is loaded once in public/index.html; this module never loads a
// second GA loader (that would double-count). It only *uses* window.gtag.

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID || 'G-0WR563XR92';

/**
 * Fire a GA4 custom event. Safe to call anywhere — if gtag isn't present
 * (SSR, ad-blocker, tests) it silently no-ops. Analytics must NEVER break the app.
 * @param {string} name  snake_case event name from the spec
 * @param {object} [params]  enum/id/bool/count params only — no free text
 */
export function trackEvent(name, params = {}) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', name, params);
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', name, params);
    }
  } catch (_) {
    /* analytics must never throw into product code */
  }
}

/**
 * Fire a page_view for the given SPA path. index.html sets send_page_view:false
 * so this owns ALL pageviews (no double-count on the initial load).
 * @param {string} path  location.pathname + location.search
 */
export function trackPageView(path) {
  try {
    const href =
      (typeof window !== 'undefined' && window.location && window.location.href) || path;
    const title = (typeof document !== 'undefined' && document.title) || '';
    trackEvent('page_view', {
      page_path: path,
      page_location: href,
      page_title: title,
    });
  } catch (_) {
    /* no-op */
  }
}

// --- Low-cardinality bucketing helpers (keep GA param values tiny) ----------

export function durationBucket(ms) {
  if (ms == null || Number.isNaN(ms)) return 'unknown';
  const s = ms / 1000;
  if (s < 1) return '<1s';
  if (s < 5) return '1-5s';
  if (s < 15) return '5-15s';
  if (s < 60) return '15-60s';
  if (s < 300) return '1-5m';
  return '>5m';
}

export function scoreBucket(score) {
  const n = Number(score);
  if (Number.isNaN(n)) return 'unknown';
  if (n <= 0) return '0';
  if (n <= 10) return '1-10';
  if (n <= 50) return '11-50';
  if (n <= 100) return '51-100';
  return '100+';
}
